// NOTE: This require will be replaced with `@sentry/browser` when
// process.browser === true thanks to the webpack config in next.config.js
const Sentry = require('@sentry/node');
const SentryIntegrations = require('@sentry/integrations');
const getConfig = require('next/config').default;
const { maskIdsFromUrl } = require('./urlHelpers');

const { getStrictlyNecessaryServerCookie } = require('./cookies/server');

const getCustomFingerprint = (err) => {
  let fingerprint = ['{{ default }}'];
  if (err.message) {
    if (
      /window\.fbq/.test(err.message) ||
      /Object doesn't support property or method 'fbq/.test(err.message)
    ) {
      fingerprint = ['Error: window.fbq undefined'];
    } else if (/unable to get order target/i.test(err.message)) {
      fingerprint = ['Error: Failed to fetch Order Targets'];
    } else {
      // De-duplication currently doesn't work correctly for SSR / browser errors
      // so we force deduplication by error message if it is present
      fingerprint = [err.message];
    }
  }

  return fingerprint;
};

module.exports = () => {
  const config = getConfig() || { publicRuntimeConfig: {} };
  const {
    sentryDsn,
    environmentName,
    hopperReleaseId,
    hopperInstanceId,
    hopperTaskId,
    hopperImageTag,
  } = config.publicRuntimeConfig;

  const sentryOptions = {
    dsn: sentryDsn,
    release: hopperReleaseId,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    environment: environmentName,
    beforeSend(event, hint) {
      // Filtering out cookies via the Sentry UI isn't working as expected. This will prevent cookies being sent with any events.
      if (event.request && event.request.cookies) {
        delete event.request.cookies; // eslint-disable-line no-param-reassign
      }

      // As recommemnded by sentry docs:
      // https://docs.sentry.io/data-management/rollups/?platform=browser
      /* eslint-disable-next-line no-param-reassign */
      event.fingerprint = getCustomFingerprint(hint.originalException);
      return event;
    },
  };

  // When we're developing locally
  if (process.env.NODE_ENV !== 'production') {
    /* eslint-disable-next-line global-require */
    const sentryTestkit = require('sentry-testkit');
    const { sentryTransport } = sentryTestkit();

    // Don't actually send the errors to Sentry
    sentryOptions.transport = sentryTransport;

    // Instead, dump the errors to the console
    sentryOptions.integrations = [
      new SentryIntegrations.Debug({
        // Trigger DevTools debugger instead of using console.log
        debugger: false,
      }),
    ];
  }

  Sentry.init(sentryOptions);

  return {
    Sentry,
    setSentryContext: (ctx) => {
      if (ctx) {
        Sentry.configureScope((scope) => {
          const { req = {}, res = {}, pathname = '', unverifiedUserId } = ctx;

          if (res && res.statusCode) {
            scope.setExtra('statusCode', res.statusCode);
          }

          const filteredUrl = maskIdsFromUrl(req.url);

          scope.setTags({
            context: process.browser ? 'client' : 'server',
            url: filteredUrl,
            locale: req.locale,
          });

          scope.setExtras({
            pathname: maskIdsFromUrl(pathname),
            url: filteredUrl,
            method: req.method,
            headers: req.headers,
            params: req.params,
            query: req.query,
          });

          if (req.country) {
            scope.setTag('country', req.country.isoCode);
          }

          if (req.sentryTransaction) {
            scope.setTransaction(req.sentryTransaction);
          }

          const id = getStrictlyNecessaryServerCookie(req, 'roo_guid');
          if (id) {
            scope.setUser({ id });
          }

          if (unverifiedUserId) {
            scope.setTag('user_id', unverifiedUserId);
          }

          if (hopperInstanceId) {
            scope.setTags({
              hopper_ecs_instance_id: hopperInstanceId,
              hopper_ecs_task_id: hopperTaskId,
              hopper_image_tag: hopperImageTag,
              hopper_release_id: hopperReleaseId,
            });
          }
        });
      }
    },
    captureException: (err) => {
      Sentry.configureScope((scope) => {
        if (err.statusCode) {
          scope.setExtra('statusCode', err.statusCode);
        }
      });
      return Sentry.captureException(err);
    },
    getCustomFingerprint,
  };
};
